import React from 'react';

import {Sequence} from 'components/Sequence';
import {getTranslationNameLabelProperties} from 'molbio/maps/linear/TranslationDisplayUtil';
import Type from 'molbio/maps/Type';
import Translation from 'molbio/translations/backbone/Translation';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/floating-ui/Tooltip';

export interface TranslationNameLabelProps {
  aminoAcidIndicesVisible: boolean;
  fontType: Type;
  indexType: Type;
  sequence: Sequence;
  translation: Translation;
  xFn: (index: number) => number;
  // Start and end positions of the translation on the sequence capped by the start/end of this slice
  displayStart: number;
  displayEnd: number;
  handleClick: (e: React.MouseEvent) => void;
  handleDoubleClick: (e: React.MouseEvent) => void;
  openContextMenu: (e: React.MouseEvent) => void;
}

export default function TranslationNameLabel({
  aminoAcidIndicesVisible,
  fontType,
  indexType,
  sequence,
  translation,
  xFn,
  displayStart,
  displayEnd,
  handleClick,
  handleDoubleClick,
  openContextMenu,
}: TranslationNameLabelProps): JSX.Element {
  const xTranslate = xFn(displayStart);
  const translationWidth = xFn(displayEnd) - xTranslate;
  const nameLabelProperties = getTranslationNameLabelProperties({
    sequence,
    translation,
    fontType,
    indexHeight: aminoAcidIndicesVisible ? indexType.height() : 0,
    width: translationWidth,
    xTranslate,
  });

  function onContextMenu(e: React.MouseEvent): void {
    handleClick(e);
    openContextMenu(e);
  }

  return (
    <Tooltip
      text={nameLabelProperties.fullTooltipName}
      isDisabled={
        nameLabelProperties.fullTooltipName == null || nameLabelProperties.fullTooltipName.length === 0
      }
    >
      <g
        onMouseDown={handleClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={onContextMenu}
        {...getTestProps({component: 'translationsView', element: 'root'})}
      >
        {/* Line that spans the entire translation */}
        <rect
          width={nameLabelProperties.lineWidth}
          height={nameLabelProperties.lineHeight}
          x={nameLabelProperties.lineX}
          y={nameLabelProperties.lineY}
          fill={nameLabelProperties.fill}
          {...getTestProps({component: 'TranslationNameLabel', element: 'line'})}
        />
        {/* Box at the start of the translation with the name */}
        <rect
          width={nameLabelProperties.nameBoxWidth}
          height={nameLabelProperties.nameBoxHeight}
          x={nameLabelProperties.nameBoxX}
          y={nameLabelProperties.nameBoxY}
          fill={nameLabelProperties.fill}
          {...getTestProps({component: 'TranslationNameLabel', element: 'nameBox'})}
        />
        <text
          fontSize={nameLabelProperties.nameFontSize}
          x={nameLabelProperties.nameTextX}
          y={nameLabelProperties.nameTextY}
          pointerEvents="none"
          {...getTestProps({component: 'TranslationNameLabel', element: 'nameText'})}
        >
          {nameLabelProperties.displayName}
        </text>
      </g>
    </Tooltip>
  );
}
